import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import authReducer from './slices/authSlice';
import uiReducer from './slices/uiSlice';
import favoritesReducer from './slices/favoritesSlice';
import bookingDraftReducer from './slices/bookingDraftSlice';
import preferencesReducer from './slices/preferencesSlice';
import searchLocationReducer from './slices/searchLocationSlice';
import { baseApi } from './api/baseApi';
import { loggerMiddleware } from './middleware/logger';
import { resetCacheOnAccountChange } from './middleware/resetCacheOnAccountChange';

export const store = configureStore({
  reducer: {
    auth: authReducer,
    ui: uiReducer,
    favorites: favoritesReducer,
    bookingDraft: bookingDraftReducer,
    preferences: preferencesReducer,
    searchLocation: searchLocationReducer,
    [baseApi.reducerPath]: baseApi.reducer,
  },
  // logger goes last so it observes the final state after every other middleware (including
  // baseApi's) has already run.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Both of these dev-only checks walk the entire state tree on every single dispatch. The
      // RTK Query cache under `api` is by far the largest branch (every shop search, profile and
      // service list the user has viewed) and it grows as they browse, so scanning it was pushing
      // dispatches past the 32ms warning threshold and into ~190ms. RTK Query already guarantees
      // its own slice is only ever updated immutably and serialisably, so re-verifying it every
      // dispatch is wasted work — the checks still cover our own slices, which is where an
      // accidental mutation would actually be a bug.
      immutableCheck: { ignoredPaths: [baseApi.reducerPath] },
      serializableCheck: { ignoredPaths: [baseApi.reducerPath] },
    })
      .concat(baseApi.middleware)
      .concat(resetCacheOnAccountChange)
      .concat(loggerMiddleware),
});

// Enables refetchOnFocus/refetchOnReconnect for any query opted in via those options — without
// this, RTK Query only ever refetches on mount or cache expiry (60s default), so screens the user
// returns to (e.g. reopening the app) can show stale data like a stylist's newly added service.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
